"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.datadogBeforeSend = void 0;
exports.scrubUrl = scrubUrl;
exports.scrubSensitiveData = scrubSensitiveData;
exports.sanitizeErrorForTracking = sanitizeErrorForTracking;
// Central URL scrubber: masks sensitive path segments and redacts risky query params.
function scrubUrl(raw) {
    try {
        // In case raw is relative, resolve against current origin
        var url = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'https://example.com');
        // Redact common sensitive query params
        var SENSITIVE_PARAMS = [
            'token',
            'api_key',
            'apikey',
            'sessionid',
            'access_token',
            'code',
            'auth',
            'key',
            'password',
            'refresh_token',
            'id_token'
        ];
        var SENSITIVE = new Set(SENSITIVE_PARAMS.map(function (p) { return p.toLowerCase(); }));
        for (var _i = 0, _a = Array.from(url.searchParams.keys()); _i < _a.length; _i++) {
            var name_1 = _a[_i];
            if (!SENSITIVE.has(name_1.toLowerCase()))
                continue;
            var count = url.searchParams.getAll(name_1).length;
            url.searchParams.delete(name_1);
            for (var i = 0; i < count; i++)
                url.searchParams.append(name_1, '[REDACTED]');
        }
        // Redact sensitive path segments
        var pathname = url.pathname;
        var PATH_MASKS = [
            [/\/records\/[^/]+/g, '/records/[REDACTED]'],
            [/\/users\/[^/]+/g, '/users/[REDACTED]'],
            [/\/apps\/[^/\s?]+/g, '/apps/[REDACTED]'],
            [/\/applications\/[^/\s?]+/g, '/applications/[REDACTED]'],
            [/\/(reset-password|set-password|confirm|verify)\/[^/\s?]+/g, '/$1/[REDACTED]'],
            [/\/connections\/[^/\s?]+/g, '/connections/[REDACTED]'],
            [/\/views\/[^/\s?]+/g, '/views/[REDACTED]'],
            [/\/scenes\/[^/\s?]+/g, '/scenes/[REDACTED]']
        ];
        for (var _b = 0, PATH_MASKS_1 = PATH_MASKS; _b < PATH_MASKS_1.length; _b++) {
            var _c = PATH_MASKS_1[_b], rx = _c[0], repl = _c[1];
            pathname = pathname.replace(rx, repl);
        }
        url.pathname = pathname;
        return url.toString();
    }
    catch (_d) {
        // Fallback to conservative regex replace when URL parsing fails
        return (raw
            .replace(/\/records\/[^/]+/g, '/records/[REDACTED]')
            .replace(/\/users\/[^/]+/g, '/users/[REDACTED]')
            .replace(/\/apps\/[^/\s?]+/g, '/apps/[REDACTED]')
            .replace(/\/applications\/[^/\s?]+/g, '/applications/[REDACTED]')
            .replace(/\/(reset-password|set-password|confirm|verify)\/[^/\s?]+/g, '/$1/[REDACTED]')
            .replace(/\/connections\/[^/\s?]+/g, '/connections/[REDACTED]')
            .replace(/\/views\/[^/\s?]+/g, '/views/[REDACTED]')
            .replace(/\/scenes\/[^/\s?]+/g, '/scenes/[REDACTED]')
            // Scrub query parameters while preserving URL structure
            .replace(/((?:^|[?&]))(token|api_key|apikey|sessionid|access_token|id_token|code|auth|key|password|refresh_token)=([^&#]+)/gi, '$1$2=[REDACTED]')
            // Scrub URL fragments (after #) for the same secret keys
            .replace(/(#[^?]*[?&]?)(token|api_key|apikey|sessionid|access_token|id_token|code|auth|key|password|refresh_token)=([^&#]+)/gi, '$1$2=[REDACTED]'));
    }
}
/**
 * Data Scrubbing Strategy for App Trackers like Datadog RUM or Logrocket
 *
 * This file implements comprehensive data scrubbing to prevent PII and sensitive
 * information from being sent to App Trackers. The following data types are scrubbed:
 *
 * PERSONAL INFORMATION:
 * - Email addresses
 * - Phone numbers
 * - Names (first, last, middle, title)
 * - Addresses (street, city, state, zip)
 * - Social Security Numbers
 * - Geographic coordinates (latitude/longitude)
 *
 * FINANCIAL DATA:
 * - Credit card numbers
 * - Bank account numbers
 * - Routing numbers
 * - Stripe tokens and customer IDs
 *
 * TECHNICAL IDENTIFIERS:
 * - MongoDB ObjectIDs
 * - JWT tokens
 * - API keys and session IDs
 * - Knack field keys (field_XX)
 * - Connection record IDs
 * - Application/User/Customer IDs
 * - Database connection strings
 *
 * AUTHENTICATION DATA:
 * - TOTP codes (6-digit)
 * - Base32 encoded 2FA secrets
 * - Password reset tokens
 * - Generic API tokens
 *
 * FILE DATA:
 * - File names and extensions
 * - S3 URLs and file paths
 * - Digital signatures (base64 data)
 *
 * QUERY DATA:
 * - Database query parameters
 * - Search terms and filters
 * - Sort fields
 *
 * SYSTEM DATA:
 * - IP addresses
 * - File paths (especially user directories)
 * - HTML content that might contain PII
 *
 * URL SCRUBBING:
 * - Record IDs in URL paths
 * - Connection and view identifiers
 * - Scene identifiers
 * - Query parameter values
 *
 * PRIVACY PROTECTION:
 * Add to sensitive form components for additional privacy protection:
 * <div className="dd-privacy-mask" data-dd-privacy="mask">
 *   Sensitive content
 * </div>
 */
// Core function to scrub sensitive data from text
function scrubSensitiveData(text) {
    return (text
        // Email addresses
        .replace(/\b[\w._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, '[EMAIL_REDACTED]')
        // Social Security Numbers
        .replace(/\b\d{3}-?\d{2}-?\d{4}\b/g, '[SSN_REDACTED]')
        // MongoDB ObjectIDs (24 character hex strings)
        .replace(/\b[0-9a-fA-F]{24}\b/g, '[ID_REDACTED]')
        // File paths (Windows and Unix)
        .replace(/\/Users\/[^/\s]+/g, '/Users/[REDACTED]')
        .replace(/C:\\Users\\[^\\]+/g, 'C:\\Users\\[REDACTED]')
        // Credit card numbers
        .replace(/\b(?:\d{4}[-\s]?){3}\d{4}\b/g, '[CARD_REDACTED]')
        // Phone numbers (US format - configurable)
        .replace(/\b(\+?1[-.\s]?)?\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}\b/g, '[PHONE_US_REDACTED]')
        // Street addresses
        .replace(/\b\d{1,5}\s+[\w\s]+(?:street|st|avenue|ave|road|rd|lane|ln|drive|dr|blvd|boulevard|way|court|ct|place|pl)\b/gi, '[ADDRESS_REDACTED]')
        // ZIP codes (US format - 5 or 9 digit)
        .replace(/\b\d{5}(?:-\d{4})?\b/g, '[ZIP_US_REDACTED]')
        // GPS coordinates (enhanced pattern)
        .replace(/\b[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)\b/g, '[COORDINATES_REDACTED]')
        // Bank account numbers (8-12 digits)
        .replace(/\b\d{8,12}\b/g, '[ACCOUNT_NUMBER_REDACTED]')
        // Routing numbers (9 digits)
        .replace(/\b\d{9}\b/g, '[ROUTING_NUMBER_REDACTED]')
        // JWT tokens
        .replace(/eyJ[A-Za-z0-9_-]+\.eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, '[JWT_REDACTED]')
        // Stripe tokens and customer IDs
        .replace(/(sk_|pk_|cus_)[a-zA-Z0-9]{14,}/g, '[STRIPE_KEY_REDACTED]')
        // Knack-specific IDs (enhanced pattern)
        .replace(/\b(field_|object_|scene_|view_|profile_)[a-zA-Z0-9]+\b/g, '[KNACK_ID_REDACTED]')
        // Generic API tokens (32+ character alphanumeric)
        .replace(/\b[A-Za-z0-9]{32,}\b/g, '[TOKEN_REDACTED]')
        // Database connection strings
        .replace(/(mongodb|mysql|postgres):\/\/[^\s]+/g, '[DATABASE_URL_REDACTED]')
        // Names in proper case (2-3 parts, realistic length bounds)
        .replace(/\b[A-Z][a-z]{1,15}(?:\s+[A-Z]\.?)?\s+[A-Z][a-z]{1,20}\b/g, '[NAME_REDACTED]')
        // IP addresses (IPv4 and IPv6)
        .replace(/\b(?:[0-9]{1,3}\.){3}[0-9]{1,3}\b/g, '[IP_REDACTED]')
        .replace(/\b(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}\b/g, '[IPV6_REDACTED]')
        .replace(/\b::(?:[0-9a-fA-F]{1,4}:){0,6}[0-9a-fA-F]{1,4}\b/g, '[IPV6_REDACTED]')
        .replace(/\b(?:[0-9a-fA-F]{1,4}:){1,7}:\b/g, '[IPV6_REDACTED]')
        // Personal name fields
        .replace(/\b(first|last|middle|title)["':\s]*["']?[A-Za-z\s]{2,}["']?/gi, '$1=[NAME_REDACTED]')
        // Address components
        .replace(/\b(street|city|state|zip|postal|address)["':\s]*["']?[^"',\s]{3,}["']?/gi, '$1=[ADDRESS_REDACTED]')
        // Connection record IDs
        .replace(/(parent_field_record_ids|recordId|objectKey)=[\w,]+/gi, '$1=[RECORD_IDS_REDACTED]')
        // File names
        .replace(/\b[\w-]+\.(jpg|jpeg|png|gif|pdf|doc|docx|xls|xlsx|txt|csv)\b/gi, '[FILENAME_REDACTED]')
        // S3 URLs
        .replace(/https?:\/\/[\w.-]+\.s3[\w.-]*\.amazonaws\.com\/[^\s"']+/g, '[S3_URL_REDACTED]')
        // Query parameters
        .replace(/(sort_field|filters|search)=[\w%+.-]+/gi, '$1=[QUERY_PARAM_REDACTED]')
        // TOTP codes
        .replace(/\b\d{6}\b/g, '[TOTP_CODE_REDACTED]')
        // Base32 encoded secrets (TOTP standard lengths: 16, 32, 52 chars)
        .replace(/\b[A-Z2-7]{16}\b/g, '[TOTP_SECRET_REDACTED]')
        .replace(/\b[A-Z2-7]{32}\b/g, '[TOTP_SECRET_REDACTED]')
        .replace(/\b[A-Z2-7]{52}\b/g, '[TOTP_SECRET_REDACTED]')
        .replace(/otpauth:\/\/[^\s]+/g, '[OTPAUTH_URL_REDACTED]')
        // Digital signatures (base64 data)
        .replace(/data:image\/[^;]+;base64,[A-Za-z0-9+/=]+/g, '[SIGNATURE_DATA_REDACTED]')
        // HTML content that might contain PII
        .replace(/<[^>]*>[^<]*<\/[^>]*>/g, '[HTML_CONTENT_REDACTED]')
        // RudderStack/Analytics IDs (enhanced pattern)
        .replace(/(userId|groupId|accountId|applicationId|customerId)["':\s]*["']?[A-Za-z0-9]{20,}["']?/gi, '$1=[REDACTED]'));
}
// Helper function to sanitize errors for tracking
function sanitizeErrorForTracking(error) {
    var sanitizedError = new Error(error.message);
    sanitizedError.name = error.name;
    sanitizedError.stack = error.stack;
    // Scrub sensitive data from error message
    if (sanitizedError.message) {
        sanitizedError.message = scrubSensitiveData(sanitizedError.message);
    }
    // Scrub sensitive data from stack trace
    if (sanitizedError.stack) {
        sanitizedError.stack = scrubSensitiveData(sanitizedError.stack);
    }
    return sanitizedError;
}
// Datadog RUM beforeSend handler that scrubs sensitive data from events
var datadogBeforeSend = function (event) {
    var _a, _b, _c, _d;
    // Scrub sensitive URL parameters
    if ((_a = event.view) === null || _a === void 0 ? void 0 : _a.url) {
        event.view.url = scrubUrl(event.view.url);
    }
    if ((_b = event.view) === null || _b === void 0 ? void 0 : _b.referrer) {
        event.view.referrer = scrubUrl(event.view.referrer);
    }
    // Resource events
    if ((_c = event.resource) === null || _c === void 0 ? void 0 : _c.url) {
        event.resource.url = scrubUrl(event.resource.url);
    }
    // Remove sensitive error details
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((_d = event.error) === null || _d === void 0 ? void 0 : _d.message) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        var errorObj = event.error;
        errorObj.message = scrubSensitiveData(errorObj.message);
        if (errorObj.stack) {
            errorObj.stack = scrubSensitiveData(errorObj.stack);
        }
    }
    return true;
};
exports.datadogBeforeSend = datadogBeforeSend;
//# sourceMappingURL=index.js.map